import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import CustomModal from "../common/Modal";
import Button from "../sub-component/Button";
import PricingSwitch from "../sub-component/PricingSwitch";
import { ReactComponent as CrossIcon } from "../../assets/icons/components/common/modal/cross.svg";
import { getSubscriptionProducts } from "../../redux/thunks/Subscription";
import { useDispatch, useSelector } from "react-redux";
import { useMediaQuery } from "@mui/material";

import { ReactComponent as CheckCircleIcon } from "../../assets/icons/basic/Check icon.svg";
import { ReactComponent as CloseIcon } from "../../assets/icons/basic/x.svg";
import { ReactComponent as IconFileSearch } from "../../assets/icons/pricing/Lite/file-search.svg";
import { ReactComponent as IconShield } from "../../assets/icons/pricing/Lite/shield.svg";
import { ReactComponent as IconTarget } from "../../assets/icons/pricing/Lite/target.svg";
import { ReactComponent as IconZap } from "../../assets/icons/pricing/Lite/zap.svg";
import { ReactComponent as IconClipboardTick } from "../../assets/icons/pricing/Lite/clipboard-tick.svg";
import { ReactComponent as IconSend } from "../../assets/icons/pricing/Lite/send.svg";
import { ReactComponent as IconBookOpen } from "../../assets/icons/pricing/Lite/book-open.svg";
import { ReactComponent as IconClipboard } from "../../assets/icons/pricing/Lite/clipboard.svg";
// import { ReactComponent as IconUsers } from "../../assets/icons/pricing/Lite/users.svg";
import { ReactComponent as IconFolderNew } from "../../assets/icons/pricing/Growth/folder.svg";
import { ReactComponent as IconGitCommit } from "../../assets/icons/pricing/Growth/git-commit.svg";
import { ReactComponent as IconLayersNew } from "../../assets/icons/pricing/Scale/layers.svg";
import { ReactComponent as IconFileText } from "../../assets/icons/pricing/Scale/file-text.svg";
import { ReactComponent as IconUsers } from "../../assets/icons/pricing/Enterprise/users.svg";
import { ReactComponent as IconMonitor } from "../../assets/icons/pricing/Enterprise/monitor.svg";
import { ReactComponent as IconSettings } from "../../assets/icons/pricing/Enterprise/settings.svg";

const PLAN_NAMES = ["Lite", "Growth", "Scale", "Enterprise"];

const FEATURE_COMPARISON = [
  {
    name: "Job Fit Score",
    values: { Lite: true, Growth: true, Scale: true, Enterprise: true },
  },
  {
    name: "Unlimited Assessments",
    values: { Lite: true, Growth: true, Scale: true, Enterprise: true },
  },
  {
    name: "Complete Test Library",
    values: { Lite: true, Growth: true, Scale: true, Enterprise: true },
  },
  {
    name: "Qualifying Questions",
    values: { Lite: true, Growth: true, Scale: true, Enterprise: true },
  },
  {
    name: "Unlimited Users",
    values: { Lite: true, Growth: true, Scale: true, Enterprise: true },
  },
  {
    name: "Resume Analysis",
    values: { Lite: true, Growth: true, Scale: true, Enterprise: true },
  },
  {
    name: "AI Assessment Creation",
    values: { Lite: true, Growth: true, Scale: true, Enterprise: true },
  },
  {
    name: "Anti-Cheat Suite",
    values: { Lite: true, Growth: true, Scale: true, Enterprise: true },
  },
  {
    name: "Standard Integrations",
    values: { Lite: false, Growth: true, Scale: true, Enterprise: true },
  },
  {
    name: "Client Workspaces",
    values: { Lite: false, Growth: true, Scale: true, Enterprise: true },
  },
  {
    name: "Custom Test Packs",
    values: {
      Lite: false,
      Growth: false,
      Scale: "2/month",
      Enterprise: "Custom",
    },
  },
  {
    name: "Branded Evaluation Reports (PDF)",
    values: { Lite: false, Growth: false, Scale: true, Enterprise: true },
  },
  {
    name: "Dedicated Account Manager",
    values: { Lite: false, Growth: false, Scale: false, Enterprise: true },
  },
  {
    name: "Training Hours Included",
    values: { Lite: false, Growth: false, Scale: false, Enterprise: true },
  },
  {
    name: "Custom Integrations",
    values: { Lite: false, Growth: false, Scale: false, Enterprise: true },
  },
];

const STRIPE_NAME_BY_PLAN = {
  Lite: { month: "Lite-v2", year: "Lite-yearly-v2" },
  Growth: { month: "Growth", year: "Growth-yearly" },
  Scale: { month: "Scale", year: "Scale-yearly" },
};

const Plan = (props) => {
  const isOnWebScreen = useMediaQuery("(min-width:1280px)");
  const isSmallScreen = useMediaQuery("(max-width:900px)");
  // const isOnTabletScreen = useMediaQuery("(max-width: 1200px)");
  const {
    isUpdatePlan,
    selectedPlan,
    handleCloseModal,
    tenant_status,
    processing,
    showFeatureComparison,
    setShowFeatureComparison,
  } = props;
  const dispatch = useDispatch();
  const [openContactUsForm, setOpenContactUsForm] = useState(false);
  const [checkedYearlySwitch, setCheckedYearlySwitch] = useState(false);
  const [showAllLiteFeatures, setShowAllLiteFeatures] = useState(false);
  const { userProfileData } = useSelector((state) => state.userProfileReducer);
  const subscription = useSelector((state) => state.subscriptionReducer);
  const hubspotMeetingsLink = `${window._env_.HUBSPOT_MEETINGS_LINK}&firstname=${encodeURIComponent(userProfileData?.first_name)}&lastname=${encodeURIComponent(userProfileData?.last_name)}&email=${encodeURIComponent(userProfileData?.email)}`;

  const handleContactUs = () => {
    setOpenContactUsForm(true);
  };

  const changeYearlySwitch = () => {
    setCheckedYearlySwitch(!checkedYearlySwitch);
  };

  const plans = [
    {
      name: "Lite",
      description: "For small teams getting started",
      price: checkedYearlySwitch ? "$948" : "$99",
      evaluationsInfo: checkedYearlySwitch
        ? "180 Evaluations included"
        : "15 Evaluations included",
      details: checkedYearlySwitch
        ? [
          "$6.60 per included evaluation",
          "+$7.50 per additional evaluation",
          "3-month rollover",
        ]
        : [
          "$6.60 per included evaluation",
          "+$7.50 per additional evaluation",
          "3-month rollover",
        ],
      featuresLabel: "Includes core features:",
      features: [
        {
          text: "Job Fit Score",
          icon: <IconTarget style={{ width: 20, height: 20 }} />,
        },
        {
          text: "Resume Analysis",
          icon: <IconFileSearch style={{ width: 20, height: 20 }} />,
        },
        {
          text: "AI Assessment Creation",
          icon: <IconZap style={{ width: 20, height: 20 }} />,
        },
        {
          text: "Anti-Cheat Suite",
          icon: <IconShield style={{ width: 20, height: 20 }} />,
        },
      ],
      extraFeatures: [
        {
          text: "Unlimited Assessments",
          icon: <IconClipboardTick style={{ width: 20, height: 20 }} />,
        },
        {
          text: "Unlimited Candidates Invites",
          icon: <IconSend style={{ width: 20, height: 20 }} />,
        },
        {
          text: "Complete Test Library Access",
          icon: <IconBookOpen style={{ width: 20, height: 20 }} />,
        },
        {
          text: "Qualifying Questions",
          icon: <IconClipboard style={{ width: 20, height: 20 }} />,
        },
        {
          text: "Unlimited Users",
          icon: <IconUsers style={{ width: 20, height: 20 }} />,
        },
      ],
    },
    {
      name: "Growth",
      description: "Growing teams and agencies",
      price: checkedYearlySwitch ? "$2,868" : "$299",
      evaluationsInfo: checkedYearlySwitch
        ? "720 Evaluations included"
        : "60 Evaluations included",
      details: checkedYearlySwitch
        ? [
          "$5.00 per included evaluation",
          "+$6.00 per additional evaluation",
          "3-month rollover",
        ]
        : [
          "$5.00 per included evaluation",
          "+$6.00 per additional evaluation",
          "3-month rollover",
        ],
      featuresLabel: "Everything in Lite, plus",
      features: [
        {
          text: "Standard Integrations",
          icon: <IconGitCommit style={{ width: 20, height: 20 }} />,
        },
        {
          text: "Client Workspaces",
          icon: <IconFolderNew style={{ width: 20, height: 20 }} />,
        },
      ],
    },
    {
      name: "Scale",
      description: "For high-volume hiring teams",
      price: checkedYearlySwitch ? "$6,708" : "$699",
      evaluationsInfo: checkedYearlySwitch
        ? "2,220 Evaluations included"
        : "185 Evaluations included",
      details: checkedYearlySwitch
        ? [
          "$3.80 per included evaluation",
          "+$4.50 per additional evaluation",
          "3-month rollover",
        ]
        : [
          "$3.80 per included evaluation",
          "+$4.50 per additional evaluation",
          "3-month rollover",
        ],
      featuresLabel: "Everything in Growth, plus",
      features: [
        {
          text: "Custom Test Packs",
          subText: "(2 per month)",
          subTextColor: "#808191",
          icon: <IconLayersNew style={{ width: 20, height: 20 }} />,
        },
        {
          text: "Branded Evaluation Reports",
          subText: "(PDF)",
          icon: <IconFileText style={{ width: 20, height: 20 }} />,
        },
      ],
    },
    {
      name: "Enterprise",
      description: "For custom hiring operations",
      price: "Custom pricing",
      evaluationsInfo: "Custom evaluation volume",
      details: [
        "Flexible Pricing",
        "Flexible rollover terms",
        "Dedicated support",
      ],
      featuresLabel: "Everything in Scale, plus",
      features: [
        {
          text: "Dedicated Account Manager",
          icon: <IconUsers style={{ width: 20, height: 20 }} />,
        },
        {
          text: "Training Hours Included",
          icon: <IconMonitor style={{ width: 20, height: 20 }} />,
        },
        {
          text: "Custom Integrations",
          icon: <IconSettings style={{ width: 20, height: 20 }} />,
        },
      ],
      isContact: true,
    },
  ];

  const getStripeProduct = (
    planName,
    yearlySwitch,
    selectedPlanId,
    products,
  ) => {
    const targetName =
      STRIPE_NAME_BY_PLAN[planName]?.[yearlySwitch ? "year" : "month"];
    return products?.find(
      (p) => p.stripe_name === targetName && p.id === selectedPlanId,
    );
  };

  const isDisabled = (planName) => {
    if (["Lite", "Growth", "Scale"].includes(planName)) {
      return !!getStripeProduct(
        planName,
        checkedYearlySwitch,
        selectedPlan?.stripe_product_id,
        subscription?.stripe_products,
      );
    }
    return false;
  };

  const getButtonText = (planName, isContact) => {
    if (isContact) return "Contact Us";
    if (isDisabled(planName)) return "Current Plan";

    const planOrder = { Lite: 1, Growth: 2, Scale: 3 };
    let currentPlanName = null;
    if (isDisabled("Lite")) currentPlanName = "Lite";
    else if (isDisabled("Growth")) currentPlanName = "Growth";
    else if (isDisabled("Scale")) currentPlanName = "Scale";

    if (!currentPlanName)
      return isUpdatePlan ? `Select ${planName}` : "Start 30 days Free Trial";

    if (planOrder[planName] < planOrder[currentPlanName]) {
      return `Downgrade to ${planName}`;
    } else {
      return `Upgrade to ${planName}`;
    }
  };

  const getPlanDetails = (planName, yearlySwitch, products, plans) => {
    const targetName =
      STRIPE_NAME_BY_PLAN[planName]?.[yearlySwitch ? "year" : "month"];
    const product = products?.find((p) => p.stripe_name === targetName);
    const interval = yearlySwitch ? "year" : "month";
    switch (planName) {
      case "Lite":
        return { ...product, ...plans[0], interval };
      case "Growth":
        return { ...product, ...plans[1], interval };
      case "Scale":
        return { ...product, ...plans[2], interval };
      default:
        return null;
    }
  };

  const handleConfirmPlan = (planName, isContact) => {
    if (isContact) {
      handleContactUs();
      return;
    }
    const planDetails = getPlanDetails(
      planName,
      checkedYearlySwitch,
      subscription?.stripe_products,
      plans,
    );
    props.onConfirmPlan(planDetails);
  };

  useEffect(() => {
    if (selectedPlan?.stripe_product?.interval === "year") {
      setCheckedYearlySwitch(true);
    }
  }, [selectedPlan]);

  useEffect(() => {
    dispatch(getSubscriptionProducts());
  }, []);

  const renderComparisonCell = (value, small = false) => {
    if (value === true) {
      return (
        <CheckCircleIcon
          style={{
            width: small ? "18px" : "28px",
            height: small ? "18px" : "28px",
          }}
        />
      );
    }
    if (typeof value === "string") {
      return (
        <span className={small ? "body-3" : "body-1"}>{value}</span>
      );
    }
    return (
      <span
        style={{
          fontSize: small ? "11px" : "14px",
          width: "10px",
          height: small ? "14px" : "20px",
          color: "#121216",
        }}
      >
        -
      </span>
    );
  };

  const renderFeatureComparisonContent = () => (
    <div style={{ width: "100%" }}>
      <div
        className={`d-flex align-items-center justify-content-between ${isSmallScreen ? "mb-2" : "mb-4"}`}
        style={{ flexWrap: "wrap", gap: isSmallScreen ? "8px" : "12px" }}
      >
        <span
          className="headline-4 m-0"
          style={{ fontSize: isSmallScreen ? "16px" : "24px", fontWeight: "500" }}
        >
          Compare all features
        </span>
        <CloseIcon
          className="pointer"
          color="#808191"
          style={{ width: "12px", height: "12px" }}
          onClick={() => setShowFeatureComparison(false)}
        />
      </div>

      <div
        style={{
          border: "1px solid #E5E7EB",
          borderRadius: "8px",
          overflow: "hidden",
        }}
      >
        <table
          style={{
            width: "100%",
            borderCollapse: "collapse",
            tableLayout: "fixed",
          }}
        >
          <thead>
            <tr style={{ backgroundColor: "#fff" }}>
              <th
                style={{
                  padding: isSmallScreen ? "8px 6px" : "16px 20px",
                  textAlign: "left",
                  borderBottom: "1px solid #E8F5E9",
                  borderRight: "1px solid #E8F5E9",
                  width: isSmallScreen ? "28%" : "36%",
                }}
                className={isSmallScreen ? "body-3 semibold" : "subtitle-1"}
              >
                Features
              </th>
              {PLAN_NAMES.map((planName) => (
                <th
                  key={planName}
                  style={{
                    padding: isSmallScreen ? "8px 4px" : "16px 12px",
                    textAlign: "center",
                    borderBottom: "1px solid #E8F5E9",
                    borderRight:
                      planName !== "Enterprise" ? "1px solid #E8F5E9" : "none",
                  }}
                  className={isSmallScreen ? "body-3 semibold" : "subtitle-1"}
                >
                  {planName}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {FEATURE_COMPARISON.map((feature, rowIdx) => (
              <tr key={feature.name}>
                <td
                  style={{
                    padding: isSmallScreen ? "6px 6px" : "14px 20px",
                    borderBottom:
                      rowIdx < FEATURE_COMPARISON.length - 1
                        ? "1px solid #E8F5E9"
                        : "none",
                    borderRight: "1px solid #E8F5E9",
                    wordBreak: "break-word",
                    hyphens: "auto",
                  }}
                  className={isSmallScreen ? "body-3" : "body-1"}
                >
                  {feature.name}
                </td>
                {PLAN_NAMES.map((planName) => (
                  <td
                    key={planName}
                    style={{
                      padding: isSmallScreen ? "6px 2px" : "14px 12px",
                      textAlign: "center",
                      borderBottom:
                        rowIdx < FEATURE_COMPARISON.length - 1
                          ? "1px solid #E8F5E9"
                          : "none",
                      borderRight:
                        planName !== "Enterprise"
                          ? "1px solid #E8F5E9"
                          : "none",
                    }}
                  >
                    {renderComparisonCell(
                      feature.values[planName],
                      isSmallScreen,
                    )}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  return (
    <>
      <CustomModal
        open={openContactUsForm}
        varrient="hubspot-meeting-box"
        handleCloseModal={() => {
          setOpenContactUsForm(false);
        }}
        hubspotMeetingsLink={hubspotMeetingsLink}
      />
      <CustomModal
        open={showFeatureComparison}
        handleCloseModal={() => setShowFeatureComparison(false)}
        hideBtns
        boxStyle={{
          // width: 'min(1200px, 95vw)',
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          outline: "none",
          maxWidth: isSmallScreen ? '96vw' : '88vw',
          maxHeight: "88vh",
          overflowY: "auto",
          borderRadius: "16px",
          padding: 0,
          backgroundColor: "#fff",
        }}
        contentStyle={{
          width: "100%",
          maxWidth: "100%",
          padding: 0,
          borderRadius: "16px",
          // marginTop: "100px",
        }}
        customClass="comparison-modal"
      >
        {renderFeatureComparisonContent()}
      </CustomModal>
      <div
        className="pricing-container w-100 p-4"
        style={{ position: "relative" }}
      >
        <div className="pricing-header mb-4" style={{ width: "100%" }}>
          <div
            className="d-flex align-items-center"
            style={{ width: "100%", flexWrap: isOnWebScreen ? "nowrap" : "wrap", gap: "12px" }}
          >
            <span className="headline-4 m-0">Choose a Plan</span>
            <div
              className="d-flex align-items-center"
              style={{ flexWrap: "wrap", gap: "8px", marginLeft: "10px" }}
            >
              <span className="subtitle-2">Monthly</span>
              <PricingSwitch
                checked={checkedYearlySwitch}
                onChange={changeYearlySwitch}
              />
              <span className="subtitle-2">Yearly</span>
              <span
                className="badge d-flex align-items-center justify-content-center"
                style={{
                  backgroundColor: "#00D69A",
                  borderRadius: "6px",
                  padding: "4px 16px",
                  width: "74px",
                  height: "22px",
                }}
              >
                <span className="body-4">20% Off</span>
              </span>
            </div>
          </div>
          {isUpdatePlan && (
            <CloseIcon
              className="pointer position-absolute top-0 end-0 m-4"
              style={{ width: "12px", height: "12px" }}
              onClick={handleCloseModal}
            />
          )}
        </div>

        <div
          className="pricing-cards d-flex"
          style={{ gap: "16px", flexWrap: "wrap", width: "100%" }}
        >
          {plans.map((plan, i) => {
            const isCurrentPlan = isDisabled(plan.name) && !plan.isContact;
            return (
              <div
                key={i}
                className={`pricing-card d-flex flex-column`}
                style={{
                  flex: isOnWebScreen ? 1 : "0 0 100%",
                  width: isOnWebScreen ? undefined : "100%",
                  minWidth: isOnWebScreen ? "200px" : undefined,
                  borderRadius: "16px",
                  border: isCurrentPlan
                    ? "0.6px solid #392D694D"
                    : "0.6px solid #ECEDF1",
                  boxShadow: isCurrentPlan && "6px 6px 54px 2px #959DA51A",
                  position: "relative",
                  backgroundColor: "#ECEDF1",
                }}
              >
                {isCurrentPlan && (
                  <div
                    className="current-plan-badge"
                    style={{
                      position: "absolute",
                      top: 0,
                      right: 0,
                      // transform: "translateX(-50%)",
                      zIndex: 1,
                      backgroundColor: "#4C4679",
                      color: "#fff",
                      padding: "4px 16px",
                      fontSize: "12px",
                      fontWeight: "500",
                      borderRadius: "0 12px 0 12px",
                      whiteSpace: "nowrap",
                    }}
                  >
                    Current Plan
                  </div>
                )}
                <div
                  className="card-top text-center"
                  style={{
                    borderBottom: "1px solid #EAECEF",
                    borderRadius: isCurrentPlan ? "0" : "12px 12px 0 0",
                    paddingTop: "25px",
                    height: "146px",
                  }}
                >
                  <div
                    className="subtitle-1 dark-100"
                    style={{
                      marginBottom: "8px",
                    }}
                  >
                    {plan.name}
                  </div>
                  <div
                    className="body-2 dark-50"
                    style={{
                      marginBottom: "8px",
                    }}
                  >
                    {plan.description}
                  </div>
                  <div className="price-block d-flex justify-content-center align-items-end">
                    {plan.price === "Custom pricing" ? (
                      <span className="subtitle-1 dark-100">{plan.price}</span>
                    ) : (
                      <>
                        <span
                          className="headline-2 dark-100"
                          style={{
                            lineHeight: 1,
                          }}
                        >
                          {plan.price}
                        </span>
                        <span
                          className="body-3"
                          style={{
                            color: "#121216",
                            marginBottom: "4px",
                            marginLeft: "2px",
                          }}
                        >
                          {checkedYearlySwitch ? "/year" : "/month"}
                        </span>
                      </>
                    )}
                  </div>
                </div>

                <div
                  className="card-body bg-white d-flex flex-column"
                  style={{
                    flex: 1,
                    borderRadius: "0 0 16px 16px",
                    padding: "16px 12px 32px 12px",
                    marginTop: "4px",
                  }}
                >
                  <div
                    className="evaluations-box"
                    style={{
                      border: "1px solid #ECEDF1",
                      borderRadius: "20px",
                      padding: "8px 12px",
                      marginBottom: "14px",
                    }}
                  >
                    <div className="d-flex align-items-center mb-2">
                      {plan.price !== "Custom pricing" ? (
                        <span
                          className="subtitle-1"
                          style={{
                            marginRight: "8px",
                          }}
                        >
                          {plan.evaluationsInfo.split(" ")[0]}
                        </span>
                      ) : null}
                      <span
                        className="body-2"
                        style={{
                          fontWeight:
                            plan.price === "Custom pricing" ? "500" : "400",
                        }}
                      >
                        {plan.price !== "Custom pricing"
                          ? plan.evaluationsInfo.split(" ").slice(1).join(" ")
                          : plan.evaluationsInfo}
                      </span>
                    </div>
                    <div
                      style={{
                        borderTop: "1px solid #ECEDF1",
                        paddingTop: "12px",
                      }}
                    >
                      {plan.details.map((detail, idx) => (
                        <div
                          key={idx}
                          className="body-3"
                          style={{
                            color: "#808191",
                            marginBottom: "10px",
                            textAlign: "left",
                          }}
                        >
                          {detail}
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="features-list mb-4" style={{ flex: 1 }}>
                    <p
                      className="body-2 semibold"
                      style={{
                        marginBottom: "16px",
                        marginLeft: "12px",
                        textAlign: "left",
                      }}
                    >
                      {plan.featuresLabel}
                    </p>
                    {(plan.name === "Lite"
                      ? showAllLiteFeatures
                        ? [...plan.features, ...plan.extraFeatures]
                        : plan.features
                      : plan.features
                    ).map((feature, idx) => (
                      <div
                        key={`${plan.name}-${feature.text}-${idx}`}
                        className="d-flex align-items-center mb-3"
                        style={{ color: "#4B5563", marginLeft: "8px" }}
                      >
                        {feature.icon && (
                          <span
                            style={{
                              color: "#6D6D6D",
                              marginRight: "12px",
                              display: "flex",
                            }}
                          >
                            {feature.icon}
                          </span>
                        )}
                        <span
                          className="body-2 dark-100"
                          style={{ textAlign: "left" }}
                        >
                          {feature.text}
                          {feature.subText && (
                            <span
                              style={{
                                display: "block",
                                color: feature.subTextColor || "inherit",
                              }}
                            >
                              {feature.subText}
                            </span>
                          )}
                        </span>
                      </div>
                    ))}
                    {plan.name === "Lite" && (
                      <button
                        className="body-3 text-left w-100"
                        type="button"
                        onClick={() => setShowAllLiteFeatures((prev) => !prev)}
                        style={{
                          color: "#3B82F6",
                          textDecoration: "none",
                          background: "none",
                          border: "none",
                          padding: 0,
                          cursor: "pointer",
                          textAlign: "left",
                        }}
                      >
                        {showAllLiteFeatures ? "Show less" : "View all"}
                      </button>
                    )}
                  </div>

                  <Button
                    btn="ragular-btn"
                    className="description"
                    varrient={isCurrentPlan ? "primary" : "primary"}
                    processing={processing && !plan.isContact}
                    disabled={isCurrentPlan && !plan.isContact}
                    onClick={() => handleConfirmPlan(plan.name, plan.isContact)}
                    title={getButtonText(plan.name, plan.isContact)}
                    style={{
                      width: "100%",
                      height: "52px",
                      backgroundColor: isCurrentPlan
                        ? "rgba(57, 45, 105, 0.8)"
                        : "#392D69",
                      color: isCurrentPlan ? "#fff" : "#fff",
                      border: "none",
                      borderRadius: "4px",
                    }}
                    titleStyle={{
                      fontFamily: "Roboto",
                      fontWeight: 500,
                      fontStyle: "Medium",
                      fontSize: 16,
                      lineHeight: "100%",
                      letterSpacing: 0,
                    }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </>
  );
};

export default Plan;

Plan.propTypes = {
  onConfirmPlan: PropTypes.func,
};
